import { Link } from 'react-router'
import { Banner, Button, Card } from '../../design/components'
import { useStartLiveSession } from './queries'
import styles from './live.module.css'

export interface LiveSessionCardProps {
  encounterId: number
  campaignId: number
  /** Where the fight happens, if the Game Master has said. */
  locationId: number | null
  locationName: string | null
  /** The location's battle map. A place holds at most one, so this is a URL rather than a list. */
  battleMapImageUrl: string | null
  /**
   * The server's word on whether starting one would be accepted.
   *
   * One rule now, where it folded four: nothing is already running. A location and a battle map
   * stopped being preconditions when a session started opening on the cast rather than on a map.
   */
  canStart: boolean
  /** The session already running for this encounter, if there is one. */
  liveSessionId: string | null
}

/**
 * Start a live session, or go to the one already running.
 *
 * **Every condition here is the server's answer, not a rule re-derived on the client.**
 * `canStartLiveSession` used to fold four clauses and now folds one — nothing already running —
 * because a session no longer begins on a map. It begins on the cast, and the place it happens is
 * chosen from inside the session.
 *
 * So the location and the battle map are still reported here, and they still write a sentence, but
 * that sentence is now *advice* rather than a refusal: a Game Master who has not drawn a map can
 * start anyway and run the encounter on faces alone, which is the only way a social encounter was
 * ever going to work.
 */
export function LiveSessionCard({
  encounterId,
  campaignId,
  locationId,
  locationName,
  battleMapImageUrl,
  canStart,
  liveSessionId,
}: LiveSessionCardProps) {
  const { start, failure, dismissFailure } = useStartLiveSession(encounterId)

  if (liveSessionId !== null) {
    return (
      <Card variant="magic" label="Live session" hint="Running">
        <p className={styles.note}>
          This fight is on the table now. Everyone in the campaign can watch it; you move the
          pieces.
        </p>
        <Link to={`/live/${liveSessionId}`}>
          <Button fullWidth>Open the map</Button>
        </Link>
      </Card>
    )
  }

  return (
    <Card variant="magic" label="Live session">
      {failure !== null && (
        <Banner
          tone="error"
          actions={
            <Button variant="secondary" onClick={dismissFailure}>
              Dismiss
            </Button>
          }
        >
          {failure}
        </Banner>
      )}

      <p className={styles.note}>
        Put the encounter on everyone&apos;s screen: who is here, then the map and the turn order
        when it starts. You move things; everyone else watches.
      </p>

      {canStart ? (
        <Button fullWidth disabled={start.isPending} onClick={() => start.mutate()}>
          {start.isPending ? 'Starting…' : 'Start a live session'}
        </Button>
      ) : (
        <p className={styles.note}>Only the Game Master can start a session.</p>
      )}

      {/* Below the button rather than instead of it. Neither of these stops a session any more, so
          neither gets to be a refusal — they are the difference between an encounter that will
          have a map and one that will run on faces, said while there is still time to fix it. */}
      {locationId === null ? (
        <p className={styles.note}>
          No location yet. It runs on the cast alone until you say where it happens — which you can
          also do from inside the session.
        </p>
      ) : battleMapImageUrl === null ? (
        <>
          <p className={styles.note}>
            {locationName ?? 'That place'} has no battle map, so there will be faces and a turn
            order but nothing to move them on.
          </p>
          <Link to={`/campaigns/${campaignId}/locations/${locationId}/compose?type=BattleMap`}>
            <Button variant="secondary" fullWidth>
              Compose a battle map
            </Button>
          </Link>
        </>
      ) : null}
    </Card>
  )
}
